import type { AutomationCaseHitDto, AutomationScenarioDto } from '@tms/shared';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Dialog,
  EntityKey,
  ErrorState,
  Field,
  SearchField,
  SelectMenu,
  SelectMenuItem,
  SkeletonRows,
} from '../../../components/ui';
import { useErrorText, useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { useDebounced } from '../../../lib/use-debounced';
import { useCaseSearch, useLinkToCase } from './api';

const WHOLE_CLASS = '__class__';

/**
 * Links a scenario of the catalogue — the whole class or one of its tests — to
 * a test case found by key or title in any project. The automation side of
 * «Link an autotest» on a case.
 */
export const LinkToCaseDialog = ({
  scenario,
  onClose,
}: {
  scenario: AutomationScenarioDto;
  onClose: () => void;
}) => {
  const { t } = useT();
  const { errorText } = useErrorText();
  const [method, setMethod] = useState(WHOLE_CLASS);
  const [search, setSearch] = useState('');
  const query = useDebounced(search.trim());
  const [chosen, setChosen] = useState<AutomationCaseHitDto | null>(null);
  const found = useCaseSearch(query, true);
  const link = useLinkToCase();
  const tests = scenario.steps.filter((step) => step.method);
  const cases = found.data ?? [];

  const submit = () =>
    chosen &&
    link.mutate(
      {
        caseKey: chosen.caseKey,
        automationProjectId: scenario.projectId,
        className: scenario.className,
        testMethod: method === WHOLE_CLASS ? null : method,
      },
      {
        onSuccess: () => {
          toast.success(t('automation.caseLinks.linkedTo', { key: chosen.caseKey }));
          onClose();
        },
        onError: (error) => toast.error(errorText(error)),
      },
    );

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={t('automation.caseLinks.toCaseTitle', { name: scenario.title })}
      description={t('automation.caseLinks.toCaseHint')}
      size="md"
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={!chosen || link.isPending} onClick={submit}>
            {t('automation.caseLinks.link')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={t('automation.caseLinks.what')}>
          {({ id }) => (
            <SelectMenu id={id} value={method} onValueChange={setMethod}>
              <SelectMenuItem value={WHOLE_CLASS}>
                {t('automation.caseLinks.wholeClass')}
              </SelectMenuItem>
              {tests.map((test) => (
                <SelectMenuItem key={test.method} value={test.method ?? ''}>
                  {test.name}
                </SelectMenuItem>
              ))}
            </SelectMenu>
          )}
        </Field>
        <div className="flex flex-col gap-3">
          <div className="flex">
            <SearchField
              value={search}
              onChange={(value) => {
                setSearch(value);
                setChosen(null);
              }}
              placeholder={t('automation.caseLinks.caseSearchPlaceholder')}
              label={t('automation.caseLinks.caseSearchLabel')}
              autoFocus
            />
          </div>
          {query.length < 2 ? (
            <p className="py-2 text-[13px] text-ink-subtle">
              {t('automation.caseLinks.caseSearchStart')}
            </p>
          ) : found.error ? (
            <ErrorState error={found.error} onRetry={() => void found.refetch()} />
          ) : !found.data ? (
            <SkeletonRows rows={3} className="p-0" />
          ) : cases.length === 0 ? (
            <p className="py-2 text-[13px] text-ink-subtle">
              {t('automation.caseLinks.caseSearchNone')}
            </p>
          ) : (
            <ul
              className="flex max-h-72 flex-col overflow-y-auto"
              aria-label={t('automation.caseLinks.caseSearchResults')}
            >
              {cases.map((testCase) => {
                const on = chosen?.caseKey === testCase.caseKey;
                return (
                  <li key={testCase.caseKey}>
                    <button
                      type="button"
                      aria-pressed={on}
                      onClick={() => setChosen(testCase)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-md px-2 py-2 text-left',
                        on ? 'bg-accent-soft' : 'hover:bg-sunken',
                      )}
                    >
                      <EntityKey className="shrink-0">{testCase.caseKey}</EntityKey>
                      <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
                        {testCase.title}
                      </span>
                      {testCase.isAutomated ? (
                        <Badge>{t('automation.caseLinks.automated')}</Badge>
                      ) : null}
                      <span className="shrink-0 text-[12px] text-ink-subtle">
                        {testCase.projectName}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Dialog>
  );
};
